import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantOperationLog.css";
import { API_BASE } from "../../../ApiUrl";
import WorkflowTabs from "../WorkFlows/WorflowTabs";

const API_URL = `${API_BASE}/tenant-operation-logs/`;
const USERS_API_URL = `${API_BASE}/users/`;

const STATUS_OPTIONS = [
  { value: "STARTED", label: "Started" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" }
];

const OPERATION_TYPE_OPTIONS = [
  { value: "BULK_IMPORT", label: "Bulk Import" },
  { value: "BULK_EXPORT", label: "Bulk Export" },
  { value: "RETENTION_SWEEP", label: "Retention Sweep" },
  { value: "REPORT_GENERATION", label: "Report Generation" },
  { value: "DATA_SYNC", label: "Data Sync" },
  { value: "OTHER", label: "Other" }
];

const emptyLog = {
  operation_type: "",
  performed_by: "",
  status: "STARTED",
  remarks: "",
  completed_at: ""
};

const toDateTimeInput = (value) => {
  if (!value) return "";
  // Convert ISO datetime to the value format expected by datetime-local input
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AddTenantOperationLog = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editLog = location.state?.editLog || null;
  const isEditMode = Boolean(editLog?.id);

  const [logs, setLogs] = useState(() => {
    if (editLog) {
      return [
        {
          operation_type: editLog.operation_type || "",
          performed_by: editLog.performed_by ? String(editLog.performed_by) : "",
          status: editLog.status || "STARTED",
          remarks: editLog.remarks || "",
          completed_at: toDateTimeInput(editLog.completed_at)
        }
      ];
    }

    return [{ ...emptyLog }];
  });

  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // Load users for the performed_by dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await axios.get(USERS_API_URL);
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
        setUsersList(data);
      } catch (error) {
        console.error("Error fetching users:", error);
        // Non-blocking — the field is optional
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setLogs((prev) =>
      prev.map((log, i) => (i === index ? { ...log, [name]: value } : log))
    );
  };

  const addLog = () => {
    if (isEditMode) return;
    setLogs((prev) => [...prev, { ...emptyLog }]);
  };

  const removeLog = (index) => {
    if (isEditMode || logs.length === 1) return;
    setLogs((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRow = (log, index) => {
    const label = `Operation Log ${index + 1}`;

    if (!log.operation_type) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select an Operation Type for ${label}.`
      });
      return false;
    }

    if (log.operation_type.length > 100) {
      Swal.fire({
        icon: "warning",
        title: "Operation Type Too Long",
        text: `Operation Type for ${label} must be 100 characters or fewer.`
      });
      return false;
    }

    if (!log.status) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please select a Status for ${label}.`
      });
      return false;
    }

    if (
      log.status === "COMPLETED" &&
      !log.completed_at
    ) {
      Swal.fire({
        icon: "warning",
        title: "Completed At Required",
        text: `Please provide a Completed At timestamp for ${label} since its status is Completed.`
      });
      return false;
    }

    if (
      log.completed_at &&
      log.status !== "COMPLETED" &&
      log.status !== "FAILED" &&
      log.status !== "CANCELLED"
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Status for Completed Timestamp",
        text: `${label} has a Completed At timestamp but its status is "${log.status}". Set status to Completed, Failed or Cancelled, or clear the timestamp.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (log, tenantUser) => {
    // Convert datetime-local string ("2025-01-31T14:30") to ISO string
    let completedAtIso = null;
    if (log.completed_at) {
      const d = new Date(log.completed_at);
      if (!Number.isNaN(d.getTime())) {
        completedAtIso = d.toISOString();
      }
    }

    return {
      operation_type: log.operation_type,
      performed_by: log.performed_by ? Number(log.performed_by) : null,
      status: log.status,
      remarks: (log.remarks || "").trim(),
      completed_at: completedAtIso,
      tenant: tenantUser.tenant_id,
      user: tenantUser.user_id
      // started_at is auto_now_add — never sent
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser) {
      Swal.fire({
        icon: "error",
        title: "Session Information Missing",
        text: "Tenant user information was not found in local storage."
      });
      return;
    }

    if (!tenantUser.tenant_id || !tenantUser.user_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid Tenant User",
        text: "Tenant ID or User ID is missing from tenant_user."
      });
      return;
    }

    for (let i = 0; i < logs.length; i++) {
      if (!validateRow(logs[i], i)) return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const payload = buildPayload(logs[0], tenantUser);

        console.log("Update Tenant Operation Log Payload:", payload);

        const response = await axios.patch(
          `${API_URL}${editLog.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Update Tenant Operation Log Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "Operation Log Updated Successfully",
          text: "Tenant operation log has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/operation-logs");
        return;
      }

      const payload = logs.map((log) => buildPayload(log, tenantUser));

      console.log("Create Tenant Operation Logs Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Tenant Operation Logs Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "Operation Logs Created Successfully",
        text: `${logs.length} operation log${
          logs.length > 1 ? "s" : ""
        } created successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/operation-logs");
    } catch (error) {
      console.error(
        isEditMode
          ? "Update Tenant Operation Log Error:"
          : "Create Tenant Operation Logs Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update tenant operation log."
        : "Unable to create tenant operation logs.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Save Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/operation-logs");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-oplog-page">
          <WorkflowTabs/>
          <div className="tenant-oplog-page-header">
            <div>
              <h2>
                {isEditMode
                  ? "Edit Operation Log"
                  : "Create Operation Log"}
              </h2>
              <p>
                {isEditMode
                  ? "Update tenant operation log information."
                  : "Add one or multiple tenant operation log entries."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="tenant-oplog-card">
              <div className="tenant-oplog-form-body">
                {logs.map((log, index) => (
                  <div className="tenant-oplog-record" key={index}>
                    <div className="tenant-oplog-record-header">
                      <div>
                        <h4>
                          {isEditMode
                            ? "Operation Log Details"
                            : `Operation Log ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && logs.length > 1 && (
                        <button
                          type="button"
                          className="tenant-oplog-remove-btn"
                          onClick={() => removeLog(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="tenant-oplog-form-grid">
                      <div className="tenant-oplog-form-group">
                        <label>
                          Operation Type <span>*</span>
                        </label>
                        <select
                          name="operation_type"
                          value={log.operation_type}
                          onChange={(e) => handleChange(index, e)}
                          required
                        >
                          <option value="">Select operation type</option>
                          {OPERATION_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="tenant-oplog-form-group">
                        <label>
                          Status <span>*</span>
                        </label>
                        <select
                          name="status"
                          value={log.status}
                          onChange={(e) => handleChange(index, e)}
                          required
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="tenant-oplog-form-group">
                        <label>Performed By</label>
                        <select
                          name="performed_by"
                          value={log.performed_by}
                          onChange={(e) => handleChange(index, e)}
                          disabled={loadingUsers}
                        >
                          <option value="">
                            {loadingUsers
                              ? "Loading users..."
                              : "Scheduled / System (no user)"}
                          </option>
                          {usersList.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.full_name ||
                                user.username ||
                                user.email ||
                                `User ${user.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="tenant-oplog-form-group">
                        <label>Completed At</label>
                        <input
                          type="datetime-local"
                          name="completed_at"
                          value={log.completed_at}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </div>

                      <div className="tenant-oplog-form-group tenant-oplog-form-group--full">
                        <label>Remarks</label>
                        <textarea
                          name="remarks"
                          value={log.remarks}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Optional notes about this operation"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="tenant-oplog-add-btn"
                    onClick={addLog}
                  >
                    <span>+</span>
                    Add Another Operation Log
                  </button>
                )}
              </div>
            </div>

            <div className="tenant-oplog-form-actions">
              <button
                type="button"
                className="tenant-oplog-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="tenant-oplog-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                  ? "Update Operation Log"
                  : `Save ${logs.length} Operation Log${
                      logs.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTenantOperationLog;